import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';

interface Props {
  income: number;
  expenses: number;
  netIncome: number;
}

const DashboardSummary: React.FC<Props> = ({ income, expenses, netIncome }) => {
  const [animatedIncome, setAnimatedIncome] = useState(0);
  const [animatedExpenses, setAnimatedExpenses] = useState(0);
  const [animatedNetIncome, setAnimatedNetIncome] = useState(0);

  // Example sparkline data (mocked for now — can fetch real trends later)
  const sparklineData = [
    [1000, 1500, 1300, 1700, income],
    [2000, 1800, 1900, 2200, expenses],
    [0, 300, 400, 500, netIncome],
  ];

  useEffect(() => {
    const animate = (
      target: number,
      setter: React.Dispatch<React.SetStateAction<number>>
    ) => {
      let start = 0;
      const increment = Math.ceil(target / 30);
      const interval = setInterval(() => {
        start += increment;
        if (start >= target) {
          setter(target);
          clearInterval(interval);
        } else {
          setter(start);
        }
      }, 15);
    };

    animate(income, setAnimatedIncome);
    animate(expenses, setAnimatedExpenses);
    animate(netIncome, setAnimatedNetIncome);
  }, [income, expenses, netIncome]);

  const cards = [
    {
      label: 'Income',
      icon: '📥',
      value: animatedIncome,
      color: 'green',
      data: sparklineData[0],
    },
    {
      label: 'Expenses',
      icon: '📤',
      value: animatedExpenses,
      color: 'red',
      isNegative: true,
      data: sparklineData[1],
    },
    {
      label: 'Net Income',
      icon: '💼',
      value: animatedNetIncome,
      color: 'blue',
      isNegative: netIncome < 0,
      data: sparklineData[2],
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow border dark:border-gray-700 transition-all">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
        Summary – July 2025
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map(({ label, icon, value, color, isNegative, data }) => {
          const bg =
            color === 'green'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
              : color === 'red'
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700';

          const text =
            color === 'green'
              ? 'text-green-700 dark:text-green-300'
              : color === 'red'
              ? 'text-red-700 dark:text-red-300'
              : 'text-blue-700 dark:text-blue-300';

          return (
            <div
              key={label}
              className={`flex flex-col gap-2 px-4 py-5 rounded-xl border shadow-sm ${bg} transition-colors`}
            >
              <div className="flex items-center gap-3">
                <div className={`text-2xl w-10 h-10 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 shadow-inner ${text}`}>
                  {icon}
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">{label}</p>
                  <div className="flex items-center gap-1">
                    <p className={`text-lg font-bold ${text}`}>
                      ${value.toLocaleString()}
                    </p>
                    {isNegative !== undefined && (
                      <span className={`text-sm ${isNegative ? 'text-red-500' : 'text-green-500'}`}>
                        {isNegative ? '↓' : '↑'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Sparkline */}
              <div className="h-12 -mt-1">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.map((v, i) => ({ value: v, i }))}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : '#3b82f6'}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardSummary;
